import { useEffect } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Blocks client-side navigation and browser close/refresh when a form has unsaved data.
 *
 * @param isDirty - Whether the form has unsaved changes. Pass `open && isDirty` for dialogs.
 * @returns The blocker object from React Router — pass it to `<UnsavedChangesDialog>`.
 */
export function useFormGuard(isDirty: boolean) {
  // useBlocker requires a data router (createBrowserRouter). The app currently
  // uses <BrowserRouter> which doesn't support it, so the call throws at runtime.
  // The try-catch is safe here — the hook is still called unconditionally (no
  // Rules of Hooks violation), we just gracefully degrade to "unblocked".
  let blocker: ReturnType<typeof useBlocker>;
  try {
    blocker = useBlocker(({ currentLocation, nextLocation }) => {
      return isDirty && currentLocation.pathname !== nextLocation.pathname;
    });
  } catch {
    blocker = { state: "unblocked" as const, reset: undefined, proceed: undefined, location: undefined };
  }

  // Block browser refresh / tab close
  useEffect(() => {
    if (!isDirty) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  return blocker;
}
